/**
 * ChangePasswordDialog
 * ─────────────────────────────────────────────────────────────────────────────
 * The signed-in person changes their OWN password (POST /user/change-password:
 * current password, new password). Opened from the account menu at the top of
 * the sidebar, so it is there for every dashboard login on a Users account:
 * vendor owners, their team, and staff.
 *
 * It exists because of "Set login" (SetVendorLoginDialog): a vendor whose
 * password was set by Lusuku Support and sent to them on WhatsApp is told to
 * change it once they are in, and until now there was nowhere in the vendor
 * dashboard to do that short of the emailed reset link, which is exactly what
 * was not arriving.
 *
 * Not offered while an admin is signed in as a vendor: the server refuses it
 * then (forbidWhileImpersonating), and an admin has no business choosing a
 * vendor's password from inside their session.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AppButton, AppDialog } from "../ui";
import { apiChangeOwnPassword } from "../../api/index";

export const MIN_NEW_PASSWORD_LENGTH = 8;

/** The sentence for each field, or null. Pure, for tests. */
export const changePasswordProblems = ({ currentPassword, newPassword, confirmPassword }) => ({
  currentPassword: currentPassword ? null : "Enter the password you signed in with.",
  newPassword: !newPassword
    ? "Choose a new password."
    : newPassword.length < MIN_NEW_PASSWORD_LENGTH
      ? `Use at least ${MIN_NEW_PASSWORD_LENGTH} characters.`
      : newPassword === currentPassword
        ? "The new password must be different from the current one."
        : null,
  confirmPassword:
    confirmPassword === newPassword ? null : "The two new passwords do not match.",
});

const EMPTY = { currentPassword: "", newPassword: "", confirmPassword: "" };

const ChangePasswordDialog = ({ open, onClose }) => {
  const [values, setValues] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const firstRef = useRef(null);

  // Nothing typed survives a close.
  useEffect(() => {
    if (!open) return;
    setValues(EMPTY);
    setTouched({});
    setServerErrors({});
    setFormError(null);
    setVisible(false);
  }, [open]);

  const problems = changePasswordProblems(values);
  const errorFor = (key) => serverErrors[key] || (touched[key] ? problems[key] : null);

  const update = (key) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [key]: value }));
    setServerErrors((s) => ({ ...s, [key]: undefined }));
  };

  const submit = async () => {
    setTouched({ currentPassword: true, newPassword: true, confirmPassword: true });
    setFormError(null);
    if (Object.values(problems).some(Boolean)) return;
    setSaving(true);
    try {
      await apiChangeOwnPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success("Password changed. Use the new one next time you sign in.");
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || "Could not change the password. Please try again.";
      if (status === 401) setServerErrors({ currentPassword: message });
      else if (status === 400 && /new password/i.test(message)) setServerErrors({ newPassword: message });
      else setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, autoComplete, ref) => (
    <label className="lk-field" key={key}>
      <span className="lk-field__label">{label}</span>
      <input
        ref={ref}
        className={`lk-input${errorFor(key) ? " is-invalid" : ""}`}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        spellCheck={false}
        autoCapitalize="off"
        value={values[key]}
        onChange={update(key)}
        onBlur={() => setTouched((t) => ({ ...t, [key]: true }))}
        aria-invalid={Boolean(errorFor(key))}
      />
      {errorFor(key) ? <span className="lk-field__error">{errorFor(key)}</span> : null}
    </label>
  );

  return (
    <AppDialog
      open={open}
      onClose={() => !saving && onClose?.()}
      busy={saving}
      size="sm"
      title="Change password"
      initialFocusRef={firstRef}
      footer={
        <>
          <AppButton variant="quiet" onClick={onClose} disabled={saving}>
            Cancel
          </AppButton>
          <AppButton onClick={submit} loading={saving}>
            Change password
          </AppButton>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {field("currentPassword", "Current password", "current-password", firstRef)}
        {field("newPassword", "New password", "new-password")}
        {field("confirmPassword", "New password again", "new-password")}
        {!errorFor("newPassword") && (
          <p className="lk-field__hint" style={{ marginTop: -8, marginBottom: 12 }}>
            At least {MIN_NEW_PASSWORD_LENGTH} characters. If Lusuku Support set your password for you, choose
            one only you know.
          </p>
        )}
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#475569", cursor: "pointer" }}>
          <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
          Show passwords
        </label>
        {formError && (
          <div className="lk-note lk-note--danger" role="alert" style={{ marginTop: 12 }}>
            <i className="fas fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 3 }} />
            <span>{formError}</span>
          </div>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </AppDialog>
  );
};

export default ChangePasswordDialog;
